'use client';

import { useDeferredValue, useMemo, useState } from 'react';
import Link from '@eliadmin/core/src/components/link';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { ButtonIcon } from '../ui/button-icon';
import { Heading } from '../ui/heading';
import { Input } from '../ui/input';
import { Paragraph } from '../ui/paragraph';

export type ManufacturerGroup = {
  letter: string;
  id: string;
  manufacturers: { id: number; name: string; href: string }[];
};

function normalize(value: string) {
  return value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

export function ManufacturersIndex({ groups }: { groups: ManufacturerGroup[] }) {
  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);

  const visibleGroups = useMemo(() => {
    const needle = normalize(deferredQuery);
    if (!needle) return groups;
    return groups
      .map(group => ({
        ...group,
        manufacturers: group.manufacturers.filter(m => normalize(m.name).includes(needle)),
      }))
      .filter(group => group.manufacturers.length);
  }, [groups, deferredQuery]);

  return (
    <div className={cn('custom-container', 'flex flex-col gap-6 sm:gap-8')}>
      <div className="flex w-full flex-col items-center gap-4">
        <Input
          type="search"
          value={query}
          onChange={event => setQuery(event.target.value)}
          placeholder="Hľadať výrobcu…"
          aria-label="Hľadať výrobcu"
          before={<ButtonIcon alt="" url="/images/icons/input-search.svg" />}
          className={{ wrapper: 'w-full max-w-[560px] border border-border', main: 'pl-12' }}
        />
        <nav
          aria-label="Výrobcovia podľa písmena"
          className="flex w-full flex-wrap justify-center gap-2"
        >
          {visibleGroups.map(group => (
            <a
              key={group.id}
              href={`#${group.id}`}
              className="flex h-10 min-w-10 items-center justify-center rounded-full border border-border px-3 text-sm font-semibold transition-all hover:bg-[#FFFACC] lg:text-base"
            >
              {group.letter}
            </a>
          ))}
        </nav>
      </div>

      {visibleGroups.length ? (
        visibleGroups.map(group => (
          <div
            key={group.id}
            id={group.id}
            className="flex scroll-mt-28 flex-col gap-3 border-t border-border pt-6 sm:gap-4 md:scroll-mt-32"
          >
            <Heading title={group.letter} tag="h2" styleAs="h4" />
            <ul className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3 sm:gap-x-6 md:grid-cols-4 lg:grid-cols-5">
              {group.manufacturers.map(manufacturer => (
                <li key={manufacturer.id} className="min-w-0">
                  <Link
                    prefetch={false}
                    href={manufacturer.href}
                    className="generic-hover block break-words py-1 text-sm font-semibold text-primary lg:text-base"
                  >
                    {manufacturer.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))
      ) : (
        <Paragraph className="w-full text-center">
          Nenašli sme žiadneho výrobcu pre „{query.trim()}“.
        </Paragraph>
      )}
    </div>
  );
}
